import {
  BR_STATUS,
  getItemStatusCode,
  resolveBrUiStatus,
  isBrApprovedCode,
  sanitizeBrRejectReasonForSupplier,
  groupBrListItems,
  pickBrDisplaySections,
  hasBrApprovedDisplayDocument,
} from '../../../businessDocuments/utils/brDocument';
import {
  annotateBrItemsWithPendingNameChangeSource,
  isBrNameChangeRequestRow,
} from '../../../businessDocuments/utils/brNameChange';
import {
  loadAdminBrDocuments,
  resolveBrPrefillRejectReason,
} from '../utils/brAdmin';
import { adminService } from '../../../../_services/admin.service';

/**
 * 管理端 BR：公司上下文、列表加载、分区与更名来源（单一数据源）。
 */
export default {
  data() {
    return {
      loading: false,
      pendingItems: [],
      processedItems: [],
      documentList: [],
      nameChangeSourceList: [],
      /** GetAdminPending 更名行；更名提交 DuplicatePending 用 */
      nameChanges: [],
      cachedCurrentNameEn: '',
      cachedCurrentNameZh: '',
      /** 防止快速切公司时旧 loadPending 覆盖新结果 */
      loadPendingSeq: 0,
    };
  },
  computed: {
    empCompanyId() {
      const fromSim = this.simulatedSupplier?.empCompanyId
        ?? this.simulatedSupplier?.EmpCompanyId;
      const fromSimNum = Number(fromSim);
      if (Number.isFinite(fromSimNum) && fromSimNum > 0) {
        return fromSimNum;
      }
      const fromProp = Number(this.supplierId);
      return Number.isFinite(fromProp) && fromProp > 0 ? fromProp : 0;
    },
    supplierAccountId() {
      const fromSim = this.simulatedSupplier?.accountId
        ?? this.simulatedSupplier?.AccountId;
      const n = Number(fromSim);
      if (Number.isFinite(n) && n > 0) {
        return n;
      }
      const fromProp = Number(this.supplierId);
      return Number.isFinite(fromProp) && fromProp > 0 ? fromProp : 0;
    },
    currentNameEn() {
      const withName = this.allDisplayItems.find((x) => x.currentNameEn)
        || this.pendingItems[0]
        || this.processedItems[0];
      return withName?.currentNameEn || this.cachedCurrentNameEn || '—';
    },
    currentNameZh() {
      const withName = this.allDisplayItems.find((x) => x.currentNameZh)
        || this.pendingItems[0]
        || this.processedItems[0];
      return withName?.currentNameZh || this.cachedCurrentNameZh || '';
    },
    canRequestNameChange() {
      // nameChangeSourceList 已是 getApprovedBrForNameChangeOptions 结果
      return (this.nameChangeSourceList || []).length > 0;
    },
    allDisplayItems() {
      return [...this.pendingItems, ...this.processedItems];
    },
    pageListSections() {
      const pendingNameChanges = (this.pendingItems || []).filter(
        (item) => isBrNameChangeRequestRow(item),
      );
      const annotated = annotateBrItemsWithPendingNameChangeSource(
        this.allDisplayItems,
        pendingNameChanges,
      );
      /* 上半：已生效+最新 Submit；下半：Last update 新→旧 */
      return groupBrListItems(annotated, this.$t.bind(this), {
        effectiveSectionLabelKey: 'lbl_EMPBE_BR_List_Section_Main',
      });
    },
    currentDisplayItems() {
      return this.displaySections.currentDisplayItems;
    },
    recordItems() {
      return this.displaySections.recordItems;
    },
    displaySections() {
      return pickBrDisplaySections(this.pageListSections);
    },
  },
  watch: {
    empCompanyId: {
      immediate: true,
      handler() {
        this.loadPending();
      },
    },
  },
  methods: {
    hasBrApprovedDisplayDocument,
    itemUiStatus(item) {
      return resolveBrUiStatus(item);
    },
    isInEffectApprovedItem(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      const ui = this.itemUiStatus(item);
      return ui === BR_STATUS.READY || ui === BR_STATUS.APPROVED;
    },
    isPendingEffectiveApprovedItem(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      return this.itemUiStatus(item) === BR_STATUS.NOT_YET_EFFECTIVE;
    },
    countInEffectApprovedItems() {
      return (this.documentList || []).filter((row) => this.isInEffectApprovedItem(row)).length;
    },
    isLastInEffectApprovedItem(item) {
      return this.isInEffectApprovedItem(item) && this.countInEffectApprovedItems() <= 1;
    },
    canChangeToReject(item) {
      if (!isBrApprovedCode(item?.statusCode)) {
        return false;
      }
      if (this.isPendingEffectiveApprovedItem(item)) {
        return true;
      }
      if (!this.isInEffectApprovedItem(item)) {
        return false;
      }
      return this.countInEffectApprovedItems() > 1;
    },
    shouldShowDirectRejectBar(item) {
      return this.canChangeToReject(item) || this.isLastInEffectApprovedItem(item);
    },
    shouldShowAdminActions(item) {
      // 审批中(P)：等阿里云机器审，整栏操作区（Reject / Proceed）不展示
      if (getItemStatusCode(item) === BR_STATUS.PENDING) {
        return false;
      }
      if (item?.listKind === 'pending') {
        return true;
      }
      if (this.shouldShowDirectRejectBar(item)) {
        return true;
      }
      return getItemStatusCode(item) === BR_STATUS.REJECTED;
    },
    initRejectedDraft(item) {
      if (!item?.approvalId) return;
      const statusCode = getItemStatusCode(item);
      const isRejected = statusCode === BR_STATUS.REJECTED;
      const isPendingManual = statusCode === BR_STATUS.PENDING_MANUAL;
      // 列表字段仅作短暂兜底；正式文案以 hydrate(GetRejectReasonByApprovalId) 为准
      const rawReason = sanitizeBrRejectReasonForSupplier(
        item?.rejectReason || item?.RejectReason || '',
      );
      if (!rawReason) {
        // PA 且无有效理由：保持空闲「驳回」，等 SP hydrate
        if (!isRejected) return;
        const emptyDraft = this.ensureDraft(item.approvalId);
        emptyDraft.rejectSelected = true;
        emptyDraft.reasonText = '';
        emptyDraft.reasonError = false;
        return;
      }
      const draft = this.ensureDraft(item.approvalId);
      draft.rejectSelected = isRejected || (isPendingManual && !!rawReason);
      draft.reasonText = rawReason;
      draft.reasonError = false;
    },
    /**
     * 预填以 proc_ApprovalRequest_GetRejectReasonByApprovalId 为准
     * （RejectedReason = udf(AIRejectedReason)）。
     */
    async hydrateRejectReasonsFromApproval(item) {
      const approvalId = Number(item?.approvalId) || 0;
      if (!approvalId) return;

      const statusCode = getItemStatusCode(item);
      if (
        statusCode !== BR_STATUS.REJECTED
        && statusCode !== BR_STATUS.PENDING_MANUAL
        && !this.shouldShowDirectRejectBar?.(item)
      ) {
        return;
      }

      try {
        const res = await adminService.GetRejectReasonByApprovalId(approvalId);
        if (!res?.isSuccessful) return;
        const raw = Array.isArray(res.data) ? res.data : [];
        const draft = this.ensureDraft(approvalId);
        const listFallback = sanitizeBrRejectReasonForSupplier(
          item?.rejectReason || item?.RejectReason || draft.reasonText || '',
        );
        // SP 有 RejectedReason 时一律覆盖列表（避免列表空壳盖住正式句）
        const nextReason = sanitizeBrRejectReasonForSupplier(
          resolveBrPrefillRejectReason(raw, listFallback),
        );
        if (!nextReason) return;
        draft.reasonText = nextReason;
        draft.rejectSelected = true;
        draft.reasonError = false;
        this.$set(item, 'rejectReason', nextReason);
      } catch (e) {
        console.error('hydrateRejectReasonsFromApproval', approvalId, e);
      }
    },
    async loadPending() {
      const empCompanyId = this.empCompanyId;
      if (!empCompanyId) {
        this.pendingItems = [];
        this.processedItems = [];
        this.documentList = [];
        this.nameChangeSourceList = [];
        this.nameChanges = [];
        this.clearRejectTemplateCaches?.();
        return;
      }
      const seq = (this.loadPendingSeq = (this.loadPendingSeq || 0) + 1);
      this.loading = true;
      try {
        const result = await loadAdminBrDocuments(empCompanyId);
        if (seq !== this.loadPendingSeq) return;

        this.documentList = result.documentList || [];
        this.nameChangeSourceList = result.nameChangeSourceList || [];
        this.nameChanges = result.nameChanges || [];
        this.pendingItems = result.pendingItems || [];
        this.processedItems = result.processedItems || [];
        if (result.currentNameEn) {
          this.cachedCurrentNameEn = result.currentNameEn;
          this.cachedCurrentNameZh = result.currentNameZh || this.cachedCurrentNameZh;
        }
        this.drafts = {};
        this.clearRejectTemplateCaches?.();
        this.resetProceedLockState?.();

        const allItems = [...(this.pendingItems || []), ...(this.processedItems || [])];
        allItems.forEach((item) => this.initRejectedDraft(item));
        await Promise.all([
          this.prefetchRejectTemplates?.(allItems) || Promise.resolve(),
          ...allItems.map((item) => this.hydrateRejectReasonsFromApproval(item)),
        ]);
        if (seq !== this.loadPendingSeq) return;

        if (result.error) {
          this.$toast?.error?.(
            result.error === 'LOAD_FAILED'
              ? this.$t('lbl_EMPBE_BR_Admin_LoadFailed')
              : result.error,
          );
        } else if (result.listLoadWarning === 'LIST_LOAD_FAILED') {
          this.$toast?.error?.(this.$t('lbl_EMPBE_BR_Admin_HistoryLoadFailed'));
        }
      } catch (e) {
        if (seq !== this.loadPendingSeq) return;
        this.pendingItems = [];
        this.processedItems = [];
        this.documentList = [];
        this.nameChangeSourceList = [];
        this.nameChanges = [];
        this.clearRejectTemplateCaches?.();
        this.$toast?.error?.(e?.message || this.$t('lbl_EMPBE_BR_Admin_LoadFailed'));
      } finally {
        if (seq === this.loadPendingSeq) this.loading = false;
      }
    },
    getNameChangeDocumentList() {
      return this.documentList || [];
    },
    onNameChangeCannotOpen() {
      this.$toast?.error?.(this.$t('lbl_EMPBE_BR_NameChange_Validation_SelectBr'));
    },
  },
};
